"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, User, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
function LatestBlogPostsContent() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div data-section-id="15693" className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 h-[500px] rounded-xl bg-muted/20 animate-pulse" />
        <div className="flex flex-col gap-6">
          <div className="h-[240px] rounded-xl bg-muted/20 animate-pulse" />
          <div className="h-[240px] rounded-xl bg-muted/20 animate-pulse" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-destructive mb-4">שגיאה בטעינת המאמרים.</p>
      </div>;
  }
  if (!posts || posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-muted-foreground">לא נמצאו מאמרים כרגע. כדאי לחזור בקרוב.</p>
      </div>;
  }
  const featuredPost = posts[0];
  const sidePosts = posts.slice(1, 3);
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 group h-full">
        <Link to={featuredPost.link || "/ProfessionalBlogPage"} newTab={featuredPost.link?.startsWith("http")} className="block h-full">
          <Card className="h-full overflow-hidden border-none shadow-lg bg-card hover:shadow-xl transition-all duration-500 flex flex-col rounded-xl">
            <div className="relative w-full aspect-video lg:aspect-auto lg:h-[400px] overflow-hidden">
              {featuredPost.featuredImage ? <img src={featuredPost.featuredImage} alt={featuredPost.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="h-full w-full bg-muted flex items-center justify-center text-muted-foreground">
                  <span className="text-lg font-default">אין תמונה</span>
                </div>}
              <div className="absolute top-4 left-4 bg-secondary text-secondary-foreground px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider shadow-sm">
                מומלץ
              </div>
            </div>
            <CardContent className="flex-1 p-6 sm:p-8 flex flex-col gap-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <SafeHydrate><Calendar className="h-4 w-4" /></SafeHydrate>
                  <span className="font-default">{featuredPost.date ? new Date(featuredPost.date).toLocaleDateString("he-IL") : "לאחרונה"}</span>
                </div>
                {featuredPost.author?.name && <div className="flex items-center gap-1.5">
                    <User className="h-4 w-4" />
                    <span className="font-default">{featuredPost.author.name}</span>
                  </div>}
              </div>
              
              <h3 className="text-2xl sm:text-3xl md:text-4xl font-default font-bold text-foreground group-hover:text-ring transition-colors">
                {featuredPost.title}
              </h3>
              
              <div className="text-muted-foreground line-clamp-3 text-base sm:text-lg font-default" dangerouslySetInnerHTML={{
              __html: featuredPost.excerpt || ""
            }} />
              
              <div className="mt-auto pt-4">
                <Button variant="link" className="p-0 h-auto text-ring hover:text-ring/80 font-bold text-lg font-default">
                  לקריאת המאמר המלא <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      <div className="flex flex-col gap-6">
        {sidePosts.map((post, i) => <div key={post.id} data-index={i} className="group h-full">
            <Link to={post.link || "/ProfessionalBlogPage"} newTab={post.link?.startsWith("http")} className="block h-full">
              <Card className="h-full overflow-hidden border-none shadow-md bg-card hover:shadow-lg transition-all duration-300 flex flex-col sm:flex-row lg:flex-col rounded-xl">
                <div className="relative h-48 sm:h-auto sm:w-48 lg:w-full lg:h-48 shrink-0 overflow-hidden">
                  {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <div className="h-full w-full bg-muted flex items-center justify-center text-muted-foreground">
                      <span className="text-sm font-default">אין תמונה</span>
                    </div>}
                </div>
                <CardContent className="p-5 flex flex-col justify-center gap-3 flex-1">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <SafeHydrate><Calendar className="h-3 w-3" /></SafeHydrate>
                    <span className="font-default">{post.date ? new Date(post.date).toLocaleDateString("he-IL") : "לאחרונה"}</span>
                  </div>
                  <h4 className="text-lg font-default font-bold text-foreground group-hover:text-ring transition-colors line-clamp-2">
                    {post.title}
                  </h4>
                  <span className="text-sm text-ring font-bold mt-auto inline-flex items-center group-hover:underline underline-offset-4 font-default">
                    קרא עוד <ArrowRight className="ml-1 h-3 w-3" />
                  </span>
                </CardContent>
              </Card>
            </Link>
          </div>)}
      </div>
    </div>;
}
export default function LatestBlogPosts() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="רשימת-פוסטים-בבלוג" className="bg-background text-foreground py-20 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold mb-4 text-foreground">
              מאמרים ועדכונים מעולם הסיעוד
            </h2>
            <p className="text-lg text-muted-foreground font-default">
              הישארו מעודכנים בחדשות האחרונות, סיפורי הצלחה ותובנות מקצועיות מצוות המומחים שלנו בתחום הטיפול והסיעוד.
            </p>
          </div>
          <Link to="#">
            <Button size="lg" className="rounded-lg px-8 bg-ring text-white hover:bg-ring/90 font-default font-bold shadow-md transition-all duration-300">
              לכל המאמרים
            </Button>
          </Link>
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestBlogPostsContent />
        </PostsProvider>
      </div>
    </section>;
}